import { XAXisOption } from 'echarts/types/src/coord/cartesian/AxisModel'
import { breakpointsTailwind, isNumber } from '@vueuse/core'
import { defineChart } from '..'
import { labelLayout } from '@/charts/world/bar'
import { tooltip } from '@/composables/useChart/theme'

const labelLineLength = 20

type Params = [Value[]]

/**
 * Create the world bar chart options.
 */
export default defineChart<Params>(values => {
    const { getName } = useCountries()

    const max = Math.max(...values.map(({ value }) => value))

    return {
        tooltip: {},
        xAxis: {},
        yAxis: {},
        grid: {
            top: '7.5%'
        },
        visualMap: {
            type: 'piecewise',
            orient: 'horizontal',
            top: 0,
            right: 0,
            showLabel: false,
            itemSymbol: 'rect',
            itemGap: 0,
            inverse: true,
            text: ['Most action   ', '   Least action'],
            hoverLink: false,
            textStyle: {
                color: 'black',
                fontSize: 14
            },
            pieces: [
                '#00282E',
                '#004851',
                '#006C7A',
                '#007373',
                '#77A48A',
                '#80C29C',
                '#A0DDBA',
                '#CAF1DB',
                '#E9FFF2'
            ]
                .map((color, index, array) => {
                    return {
                        color,
                        lte: 100 - (100 / array.length) * index
                    }
                })
                .filter(({ lte }) => lte < max)
        },
        series: {
            type: 'bar',
            tooltip: {
                formatter(params) {
                    return tooltip('', params.name, Number(params.value))
                }
            },
            barGap: '-100%',
            barCategoryGap: '15%',
            labelLine: {
                show: true,
                length2: labelLineLength,
                showAbove: true,
                lineStyle: {
                    color: 'black'
                }
            },
            label: {
                show: true,
                position: 'top',
                overflow: 'none',
                fontFamily: 'Rubik',
                align: 'left',
                color: 'black',
                minMargin: 100,
                fontSize: 12
            },
            data: values
                .filter(({ value }) => isNumber(value))
                .map(({ value, iso }) => ({
                    name: getName(iso),
                    value
                }))
                .sort((a, b) => b.value - a.value)
        },
        dataZoom: [
            {
                type: 'slider',
                orient: 'vertical',
                right: 0,
                width: 10,
                start: 0,
                end: 100,
                show: false,
                filterMode: 'filter',
                moveOnMouseMove: true,
                moveOnMouseWheel: true
            },
            {
                show: false,
                type: 'inside',
                moveOnMouseMove: true,
                moveOnMouseWheel: true
            }
        ],
        media: [
            {
                query: {
                    maxWidth: breakpointsTailwind.md
                },
                option: {
                    xAxis: createValueAxis(),
                    yAxis: {
                        ...createCategoryAxis(),
                        name: ''
                    },
                    visualMap: {
                        dimension: 0,
                        itemHeight: 10,
                        itemWidth: 15,
                        textGap: 10,
                        left: 'center'
                    },
                    dataZoom: [
                        {
                            show: true,
                            start: 90
                        },
                        {
                            show: true
                        }
                    ],
                    series: {
                        labelLayout: undefined,
                        label: {
                            position: 'right',
                            verticalAlign: 'middle'
                        },
                        emphasis: {
                            label: { show: false }
                        },
                        labelLine: {
                            show: false
                        }
                    }
                }
            },
            {
                query: {
                    minWidth: breakpointsTailwind.md
                },
                option: {
                    xAxis: createCategoryAxis(),
                    yAxis: createValueAxis(),
                    visualMap: {
                        left: 'right',
                        dimension: 1,
                        itemHeight: 20,
                        itemWidth: 30,
                        textGap: 20
                    },
                    dataZoom: [
                        {
                            show: false,
                            start: 0
                        },
                        {
                            show: false
                        }
                    ],
                    series: {
                        labelLayout: labelLayout,
                        label: {
                            position: 'top',
                            verticalAlign: 'bottom'
                        },
                        emphasis: {
                            label: { show: true }
                        },
                        labelLine: {
                            show: true
                        }
                    }
                }
            }
        ]
    }
})

function createCategoryAxis() {
    return {
        type: 'category',
        name: 'Countries'.toUpperCase(),
        z: 1000,
        axisLine: {
            lineStyle: {
                width: 2,
                color: 'black'
            }
        },
        axisTick: {
            show: false
        }
    } as XAXisOption
}

function createValueAxis() {
    return {
        type: 'value',
        name: 'total government response score (%)'.toUpperCase(),
        max: 100,
        min: -10,
        nameTruncate: {
            maxWidth: 600
        }
    }
}
